/* global axios */
import ApiClient from './ApiClient';

// Assistente de IA — tudo o que as telas do módulo pedem ao servidor.
//
// Uma classe só, com métodos nomeados como a ação que a pessoa faz na tela. As URLs seguem o
// contrato das etapas AIA-01 a AIA-04: /api/v1/accounts/:id/assistente_ia/...
class AssistenteIaAPI extends ApiClient {
  constructor() {
    super('assistente_ia', { accountScoped: true });
  }

  // --- estado e modelos -------------------------------------------------------------------
  estado() {
    return axios.get(`${this.url}/estado`);
  }

  modelos() {
    return axios.get(`${this.url}/modelos`);
  }

  // --- credenciais ------------------------------------------------------------------------
  credenciais() {
    return axios.get(`${this.url}/credenciais`);
  }

  criarCredencial(dados) {
    return axios.post(`${this.url}/credenciais`, dados);
  }

  editarCredencial(id, dados) {
    return axios.patch(`${this.url}/credenciais/${id}`, dados);
  }

  apagarCredencial(id) {
    return axios.delete(`${this.url}/credenciais/${id}`);
  }

  testarCredencial(id) {
    return axios.post(`${this.url}/credenciais/${id}/testar`);
  }

  // --- assistentes ------------------------------------------------------------------------
  assistentes() {
    return axios.get(`${this.url}/assistentes`);
  }

  assistente(id) {
    return axios.get(`${this.url}/assistentes/${id}`);
  }

  criarAssistente(dados) {
    return axios.post(`${this.url}/assistentes`, dados);
  }

  editarAssistente(id, dados) {
    return axios.patch(`${this.url}/assistentes/${id}`, dados);
  }

  apagarAssistente(id) {
    return axios.delete(`${this.url}/assistentes/${id}`);
  }

  ativarAssistente(id) {
    return axios.post(`${this.url}/assistentes/${id}/ativar`);
  }

  desativarAssistente(id) {
    return axios.post(`${this.url}/assistentes/${id}/desativar`);
  }

  buscarNaBase(id, texto) {
    return axios.post(`${this.url}/assistentes/${id}/buscar`, { texto });
  }

  playground(id, mensagens) {
    return axios.post(`${this.url}/assistentes/${id}/playground`, { mensagens });
  }

  // --- base de conhecimento ---------------------------------------------------------------
  // `emAndamento` ignora o filtro da tela: serve so para saber se ainda ha algo acontecendo.
  documentos(assistenteId, { situacao = '', page = 1, emAndamento = false } = {}) {
    const filtro = emAndamento
      ? '&em_andamento=1'
      : (situacao ? `&situacao=${situacao}` : '');
    return axios.get(
      `${this.url}/assistentes/${assistenteId}/documentos?page=${page}${filtro}`
    );
  }

  documento(assistenteId, id) {
    return axios.get(`${this.url}/assistentes/${assistenteId}/documentos/${id}`);
  }

  criarDocumento(assistenteId, dados) {
    return axios.post(
      `${this.url}/assistentes/${assistenteId}/documentos`,
      dados
    );
  }

  // O PDF vai como multipart: é arquivo, não JSON.
  enviarPdf(assistenteId, arquivo, titulo) {
    const formData = new FormData();
    formData.append('origem', 'pdf');
    formData.append('arquivo', arquivo);
    if (titulo) formData.append('titulo', titulo);

    return axios.post(
      `${this.url}/assistentes/${assistenteId}/documentos`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
  }

  apagarDocumento(assistenteId, id) {
    return axios.delete(
      `${this.url}/assistentes/${assistenteId}/documentos/${id}`
    );
  }

  reprocessarDocumento(assistenteId, id) {
    return axios.post(
      `${this.url}/assistentes/${assistenteId}/documentos/${id}/reprocessar`
    );
  }

  // --- respostas prontas ------------------------------------------------------------------
  respostasProntas(assistenteId, { page = 1 } = {}) {
    return axios.get(
      `${this.url}/assistentes/${assistenteId}/respostas_prontas?page=${page}`
    );
  }

  criarRespostaPronta(assistenteId, dados) {
    return axios.post(
      `${this.url}/assistentes/${assistenteId}/respostas_prontas`,
      dados
    );
  }

  editarRespostaPronta(assistenteId, id, dados) {
    return axios.patch(
      `${this.url}/assistentes/${assistenteId}/respostas_prontas/${id}`,
      dados
    );
  }

  apagarRespostaPronta(assistenteId, id) {
    return axios.delete(
      `${this.url}/assistentes/${assistenteId}/respostas_prontas/${id}`
    );
  }

  // --- caixas -----------------------------------------------------------------------------
  caixas() {
    return axios.get(`${this.url}/caixas`);
  }

  vincularCaixa(assistenteId, dados) {
    return axios.post(`${this.url}/assistentes/${assistenteId}/caixas`, dados);
  }

  editarCaixa(id, dados) {
    return axios.patch(`${this.url}/caixas/${id}`, dados);
  }

  desvincularCaixa(id) {
    return axios.delete(`${this.url}/caixas/${id}`);
  }

  // --- atendimentos -----------------------------------------------------------------------
  atendimentos({ situacao = '', assistenteId = '', page = 1 } = {}) {
    const filtros = [
      situacao ? `situacao=${situacao}` : '',
      assistenteId ? `assistente_id=${assistenteId}` : '',
      `page=${page}`,
    ]
      .filter(Boolean)
      .join('&');

    return axios.get(`${this.url}/atendimentos?${filtros}`);
  }

  pausarAtendimento(id) {
    return axios.post(`${this.url}/atendimentos/${id}/pausar`);
  }

  retomarAtendimento(id) {
    return axios.post(`${this.url}/atendimentos/${id}/retomar`);
  }

  // --- consumo ----------------------------------------------------------------------------
  consumo({ de = '', ate = '', assistenteId = '', tipo = '', page = 1 } = {}) {
    const filtros = [
      de ? `de=${de}` : '',
      ate ? `ate=${ate}` : '',
      assistenteId ? `assistente_id=${assistenteId}` : '',
      tipo ? `tipo=${tipo}` : '',
      `page=${page}`,
    ]
      .filter(Boolean)
      .join('&');

    return axios.get(`${this.url}/consumo?${filtros}`);
  }
}

export default new AssistenteIaAPI();
